'use client'

import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'

import type { MenuItem, MenuSeparator, NavGroup } from '@graftcode/design-system'

import { DEFAULT_WORKSPACE_NAME } from '@common/apps'
import { useProjectsStore } from '@common/modules/projects'
import type { Project } from '@common/modules/projects/types/project.types'
import { useUserStore } from '@common/modules/user'
import { getUserDisplayData } from '@common/modules/utils'
import { useWorkspacesStore } from '@common/modules/workspaces'

import { AppVariant } from '../../enums'
import { useAuthUser } from '../../hooks/useAuthUser'
import { HelpDrawer } from '../HelpDrawer'
import { getInitials } from '../Sidebar/Sidebar.helpers'

import {
  AccountDrawer,
  AppPickerDrawer,
  MobileMenuAccountSection,
  MobileMenuFooter,
  MobileMenuHeader,
  MobileMenuNavigation,
  ProjectPickerDrawer,
  ProjectsView,
  WorkspacesView,
} from './components'
import { type MenuView, MobileMenuProvider } from './context'
import { useImageLoaded } from './hooks'

import styles from './MobileMenu.module.scss'

export interface BaseMobileMenuProps {
  isOpen: boolean
  onClose: () => void
  appVariant: AppVariant
  menuGroups: NavGroup[]
  helpItems?: (MenuItem | MenuSeparator)[]
  onProfileClick?: (itemId: string) => void
  onHelpClick?: (itemId: string) => void
  onNavigationClick?: (url: string) => void
  onProjectClick?: (projectId: string) => void
  onWorkspaceClick?: (workspaceId: string) => void
  onOpenWorkspaceSettings?: () => void
  onOpenProjectSettings?: () => void
  onOpenProjectMembers?: () => void
  onInviteProjectClick?: () => void
  onInviteWorkspaceClick?: () => void
  onCreateProject?: () => void
  navigationTopSlot?: ReactNode
  renderHeader?: () => ReactNode
  showProjectSelector?: boolean
  showWorkspaceSelector?: boolean
  notificationBadge?: number
  onNotificationClick?: () => void
}

export function BaseMobileMenu({
  isOpen,
  onClose,
  appVariant,
  menuGroups,
  helpItems,
  onProfileClick,
  onHelpClick,
  onNavigationClick,
  onProjectClick,
  onWorkspaceClick,
  onOpenWorkspaceSettings,
  onOpenProjectSettings,
  onOpenProjectMembers,
  onInviteProjectClick,
  onInviteWorkspaceClick,
  onCreateProject,
  navigationTopSlot,
  renderHeader,
  showProjectSelector = true,
  showWorkspaceSelector = true,
  notificationBadge,
  onNotificationClick,
}: BaseMobileMenuProps) {
  const pathname = usePathname()
  const { user: auth0User } = useAuthUser()
  const { currentUser } = useUserStore()
  const { fullName, email } = getUserDisplayData(currentUser)
  const projects = useProjectsStore((s) => s.projects)
  const currentProject = useProjectsStore((s) => s.currentProject)
  const mobileMenuProjects = projects as Project[]

  const currentWorkspace = useWorkspacesStore((s) => s.currentWorkspace)
  const workspaces = useWorkspacesStore((s) => s.workspaces)

  const [currentView, setCurrentView] = useState<MenuView>('main')
  const [isAppPickerOpen, setIsAppPickerOpen] = useState(false)
  const [isProjectPickerOpen, setIsProjectPickerOpen] = useState(false)
  const [isAccountDrawerOpen, setIsAccountDrawerOpen] = useState(false)
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)

  const showHelpSelector = !!helpItems && helpItems.length > 0
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    onClose()
  }, [pathname]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setCurrentView('main')
        setIsScrolled(false)
      }, 300)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [isOpen])

  useEffect(() => {
    const { body, documentElement } = document
    const previousBodyOverflow = body.style.overflow
    const previousHtmlOverflow = documentElement.style.overflow

    if (isOpen) {
      body.style.overflow = 'hidden'
      documentElement.style.overflow = 'hidden'
    } else {
      body.style.overflow = previousBodyOverflow
      documentElement.style.overflow = previousHtmlOverflow
    }

    return () => {
      body.style.overflow = previousBodyOverflow
      documentElement.style.overflow = previousHtmlOverflow
    }
  }, [isOpen])

  useEffect(() => {
    const contentElement = contentRef.current
    if (!contentElement) return

    const onScroll = () => setIsScrolled(contentElement.scrollTop > 0)

    contentElement.addEventListener('scroll', onScroll, { passive: true })
    return () => contentElement.removeEventListener('scroll', onScroll)
  }, [])

  const displayUserName = auth0User?.name || fullName
  const displayUserEmail = auth0User?.email || email
  const displayProjectName = currentProject?.name || 'Select Project'
  const displayWorkspaceName = currentWorkspace?.name || DEFAULT_WORKSPACE_NAME
  const displayWorkspaceInitials = getInitials(displayWorkspaceName)

  const avatarImageLoaded = useImageLoaded(auth0User?.picture)
  const userAvatarUrl = avatarImageLoaded ? auth0User?.picture : undefined
  const isModulesMenu = appVariant === AppVariant.MODULES

  const contextValue = useMemo(
    () => ({
      currentView,
      navigateToMain: () => setCurrentView('main'),
      navigateToProjects: () => setCurrentView('projects'),
      navigateToWorkspaces: () => setCurrentView('workspaces'),
      onClose,
      onOpenWorkspaceSettings,
      onOpenProjectSettings,
      onOpenProjectMembers,
      onInviteProjectClick,
      onInviteWorkspaceClick,
      onCreateProject,
    }),
    [
      currentView,
      onClose,
      onOpenWorkspaceSettings,
      onOpenProjectSettings,
      onOpenProjectMembers,
      onInviteProjectClick,
      onInviteWorkspaceClick,
      onCreateProject,
    ]
  )

  const viewClassName = `view${currentView.charAt(0).toUpperCase() + currentView.slice(1)}`

  return (
    <MobileMenuProvider value={contextValue}>
      {isOpen && <div className={styles.overlay} onClick={() => onClose()} aria-hidden="true" />}

      <div className={`${styles.drawer} ${isOpen ? styles.open : ''} ${isModulesMenu ? styles.modulesDrawer : ''}`}>
        <div className={`${styles.viewsContainer} ${styles[viewClassName]}`}>
          <div className={styles.menuView}>
            {renderHeader ? (
              renderHeader()
            ) : (
              <MobileMenuHeader
                isScrolled={isScrolled}
                notificationBadge={notificationBadge}
                onFeedbackClick={showHelpSelector ? () => setIsHelpDrawerOpen(true) : undefined}
                onNotificationClick={onNotificationClick}
              />
            )}

            <div ref={contentRef} className={styles.content}>
              {auth0User && (
                <MobileMenuAccountSection
                  userName={displayUserName}
                  userEmail={displayUserEmail}
                  userAvatarUrl={userAvatarUrl}
                  projectName={displayProjectName}
                  onAccountSelectorClick={() => setIsAccountDrawerOpen(true)}
                  onProjectSelectorClick={() => setIsProjectPickerOpen(true)}
                  onHelpSelectorClick={() => setIsHelpDrawerOpen(true)}
                  showProjectSelector={showProjectSelector}
                  showHelpSelector={showHelpSelector}
                />
              )}

              <MobileMenuNavigation
                appVariant={appVariant}
                menuGroups={menuGroups}
                onNavigationClick={(url: string) => onNavigationClick?.(url)}
                onAppHeaderClick={() => setIsAppPickerOpen(true)}
                topSlot={navigationTopSlot}
              />
            </div>

            {auth0User && showWorkspaceSelector && (
              <MobileMenuFooter
                workspaceName={displayWorkspaceName}
                workspaceInitials={displayWorkspaceInitials}
                workspaceLabel={displayWorkspaceName}
                onWorkspaceSelectorClick={() => setCurrentView('workspaces')}
              />
            )}
          </div>

          {showProjectSelector && (
            <ProjectsView
              projects={mobileMenuProjects}
              currentProjectId={currentProject?.id}
              onProjectSelect={(projectId: string) => onProjectClick?.(projectId)}
            />
          )}

          {showWorkspaceSelector && (
            <WorkspacesView
              workspaces={workspaces}
              currentWorkspaceId={currentWorkspace?.id}
              onWorkspaceSelect={(workspaceId: string) => onWorkspaceClick?.(workspaceId)}
            />
          )}
        </div>
      </div>

      <AppPickerDrawer
        isOpen={isAppPickerOpen}
        onClose={() => setIsAppPickerOpen(false)}
        currentApp={appVariant}
        onAppSelect={(_appId: string, url: string) => {
          setIsAppPickerOpen(false)
          if (url) {
            window.location.href = url
          }
        }}
      />

      {showProjectSelector && (
        <ProjectPickerDrawer
          isOpen={isProjectPickerOpen}
          onClose={() => setIsProjectPickerOpen(false)}
          projects={mobileMenuProjects}
          currentProjectId={currentProject?.id}
          onProjectSelect={(projectId: string) => onProjectClick?.(projectId)}
          onOpenProjectSettings={onOpenProjectSettings}
          onOpenProjectMembers={onOpenProjectMembers}
          onInviteClick={onInviteProjectClick}
        />
      )}

      {auth0User && (
        <AccountDrawer
          isOpen={isAccountDrawerOpen}
          onClose={() => setIsAccountDrawerOpen(false)}
          userName={displayUserName}
          userEmail={displayUserEmail}
          userAvatarUrl={userAvatarUrl}
          onActionClick={(actionId: string) => {
            onProfileClick?.(actionId)
            onClose()
          }}
        />
      )}

      {helpItems && helpItems.length > 0 && (
        <HelpDrawer
          isOpen={isHelpDrawerOpen}
          onClose={() => setIsHelpDrawerOpen(false)}
          items={helpItems}
          onItemClick={(itemId: string) => {
            onHelpClick?.(itemId)
            onClose()
          }}
        />
      )}
    </MobileMenuProvider>
  )
}
